/* eslint-disable max-lines-per-function */
import { merge, keyBy, values, orderBy, get } from 'lodash';
import { SHORT_UI_DISPLAY_DATE } from 'constants/date/dateFormats.constant';
import DateStringTransform from 'utility/date/dateString.utility';
import styleComponent from '../../../../../../assets/stylesheets/base/_typography';
import {
  gray70,
  gray2,
} from '../../../../../../assets/stylesheets/base/_colors';
import ActivationBarGenerator from './utility/activationBar.generator';
import EnrolledActualCurveGenerator from './utility/enrolledActualCurve.generator';
import EnrolledPlannedCurveGenerator from './utility/enrolledPlannedCurve.generator';
import ScreeningCurveGenerator from './utility/screeningCurve.generator';
import PlotLineGenerator from './utility/plotline.generator';
import EnrolmentCurveAndTable from './countryDrillDownTableClass';
import DropoutCurve from './utility/dropoutCurve.generator';

class EnrolmentCurveChartTransform extends EnrolmentCurveAndTable {
  constructor() {
    super();
    this._showEnrollmentMetrics = true;
    this._showActivationMetrics = true;
    this._hasEnrollmentView = true;
  }

  set hasEnrollmentView(value) {
    this._hasEnrollmentView = value;
  }

  get hasEnrollmentView() {
    return this._hasEnrollmentView;
  }

  set showActivationMetrics(value) {
    this._showActivationMetrics = value;
  }

  get showActivationMetrics() {
    return this._showActivationMetrics;
  }

  set showEnrollmentMetrics(value) {
    this._showEnrollmentMetrics = value;
  }

  get showEnrollmentMetrics() {
    return this._showEnrollmentMetrics;
  }

  _combineData() {
    const dataSources = [];

    if (this._showActivationMetrics || !this._hasEnrollmentView) {
      dataSources.push(keyBy(this._siteActivationData, 'trialMonth'));
    }

    if (this._showEnrollmentMetrics || this._hasEnrollmentView) {
      dataSources.push(
        keyBy(this._screeningData, 'trialMonth'),
        keyBy(this._dropoutData, 'trialMonth'),
        keyBy(this._enrolledActualData, 'trialMonth'),
        keyBy(this._enrolledPlannedData, 'trialMonth'),
      );
    }

    const dateCombinedData = merge(...dataSources);

    const dateCombinedDataValues = values(dateCombinedData);
    const sortedDateCombinedData = orderBy(
      dateCombinedDataValues,
      'trialMonth',
      ['asc'],
    );
    const combinedData = sortedDateCombinedData.map((keys) => ({
      ...keys,
    }));
    return combinedData;
  }

  _generateCategories(data) {
    const categories = data.map((element) => {
      const formattedCategory = new DateStringTransform(
        element.trialMonth,
      ).formatter(SHORT_UI_DISPLAY_DATE);
      return formattedCategory;
    });
    return categories;
  }

  _generateXAxis(plotLines) {
    const xAxisConfiguration = {
      type: 'datetime',
      tickInterval: 24 * 3600 * 1000 * 30,
      crosshair: true,
      visible: true,
      dateTimeLabelFormats: {
        month: '%b %Y',
        day: '%b %Y',
        year: '%b %Y',
      },
      labels: {
        allowOverlap: false,
        padding: 7,
        style: {
          ...styleComponent('sp'),
          color: gray2,
        },
      },
      plotLines,
    };

    return xAxisConfiguration;
  }

  _getMaxY(series) {
    return Math.max(
      ...series
        .filter((sr) => sr.data && sr.data.length > 0 && sr.type !== 'area')
        .flatMap((sr) => sr.data.map((point) => point.y || 0)),
    );
  }

  _generateChart() {
    const combinedData = this._combineData();
    const { viewType } = this;

    let enrolledActualSeries = [];
    let enrolledPlannedSeries = [];
    let screeningSeries = [];
    let dropoutSeries = [];
    let activationSeries = [];

    if (this._showEnrollmentMetrics || this._hasEnrollmentView) {
      const enrolledActualCurveGenerator = new EnrolledActualCurveGenerator();
      enrolledActualSeries =
        enrolledActualCurveGenerator.getSeries(combinedData);

      const enrolledPlannedCurveGenerator = new EnrolledPlannedCurveGenerator();
      enrolledPlannedSeries =
        enrolledPlannedCurveGenerator.getSeries(combinedData);

      const screeningCurveGenerator = new ScreeningCurveGenerator();
      screeningSeries = screeningCurveGenerator.getSeries(combinedData);

      const dropoutCurveGenerator = new DropoutCurve();
      dropoutSeries = dropoutCurveGenerator.getSeries(combinedData);
    }

    if (this._showActivationMetrics || !this._hasEnrollmentView) {
      const activationCurveGenerator = new ActivationBarGenerator();
      activationSeries = activationCurveGenerator.getSeries(combinedData);
    }

    const categories = this._generateCategories(combinedData);

    const series = [
      ...activationSeries,
      ...screeningSeries,
      ...enrolledPlannedSeries,
      ...enrolledActualSeries,
      ...dropoutSeries,
    ];

    const plannedLSIDateString = get(this._plannedLSIDate, 'value', null);
    const forecastLSIDateString = get(this._forecastLSIDate, 'value', null);
    const plotLinesGenerator = new PlotLineGenerator();
    const xAxis = this._generateXAxis([
      plotLinesGenerator._getPlannedLSIPlotLine(plannedLSIDateString),
      plotLinesGenerator._getForecastLSIPlotLine(forecastLSIDateString),
    ]);
    const maxY = this._getMaxY(series);

    const enrolmentCurveYAxis = [
      {
        title: { text: 'Sites (Activation metrics)' },
        allowDecimals: false,
        visible: this._showActivationMetrics || !this._hasEnrollmentView,
      },
      {
        title: { text: 'Subjects (Enrollment metrics)' },
        opposite: true,
        plotLines: [],
        max: maxY,
        allowDecimals: false,
        visible: this._showEnrollmentMetrics || this._hasEnrollmentView,
      },
    ];

    const plotOptions = {
      series: {
        marker: {
          symbol: 'square',
          radius: 0,
        },
      },
      column: {
        maxPointWidth: 40,
      },
    };

    const legend = {
      itemStyle: {
        fontFamily: 'Open Sans',
        fontSize: '12px',
        fontWeight: 400,
        textDecoration: 'underline',
        color: gray70,
        lineHeight: '12px',
      },
      align: 'left',
      x: 50,
      squareSymbol: true,
      symbolPadding: 4,
      symbolWidth: 12,
      symbolHeight: 12,
    };

    const toolTipData = {
      useHTML: true,
      borderWidth: 0,
      backgroundColor: '#ffffff',
      formatter() {
        const pointX = this.point.x;
        const tooltipCombinedData =
          this.series.chart.options?.custom?.combinedData || [];
        const row =
          tooltipCombinedData.find(
            (data) => +new Date(data.trialMonth) === pointX,
          ) || {};
        const tooltipViewType = this.series.chart.options?.custom?.viewType;

        const sitesHeader = '<div class="tooltip-sub-header">Sites</div>';
        const sitesTable = `
          <table class="tooltip-table">
            <thead>
              <tr>
                <th>
                  <span class="th-content">
                    <span class="legend-dot planned"></span>
                    Planned activation
                  </span>
                </th>
                <th>
                  <span class="th-content">
                    <span class="legend-dot actual"></span>
                    Actual activation
                  </span>
                </th>
                <th>Activated</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${row.currPlanSitesActivatedCum ?? 0}</td>
                <td>${row.actualSitesActivatedCum ?? 0}</td>
                <td>${
                  row.currPlanSitesActivatedCum
                    ? `${Math.round(
                        (row.actualSitesActivatedCum /
                          row.currPlanSitesActivatedCum) *
                          100,
                      )}%`
                    : '--'
                }</td>
              </tr>
            </tbody>
          </table>`;

        const enrolledPercentage = row.plannedEnrolledCum
          ? `${Math.round(
              (row.actualEnrolledCum / row.plannedEnrolledCum) * 100,
            )}%`
          : '--';

        const subjectsHeader = '<div class="tooltip-sub-header">Subjects</div>';
        const subjectsTable = `
          <table class="tooltip-table">
            <thead>
              <tr>
                <th class="screen-column">
                  <span class="th-content">
                    <span class="legend-dot actual-screen"></span>
                    Actual screening
                  </span>
                </th>
                <th>
                  <span class="th-content">
                    <span class="legend-dot planned-enroll"></span>
                    Planned enrollment
                  </span>
                </th>
                <th>
                  <span class="th-content">
                    <span class="legend-dot actual-enroll"></span>
                    Actual enrollment
                  </span>
                </th>
                <th>
                  Enrolled
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="screen-column">${
                  row.actualSubjectsScreenedCum ?? 0
                }</td>
                <td>${row.plannedEnrolledCum ?? 0}</td>
                <td>${row.actualEnrolledCum ?? 0}</td>
                <td>${enrolledPercentage}</td>
              </tr>
            </tbody>
          </table>`;

        const showSites =
          (row.currPlanSitesActivatedCum ?? 0) > 0 ||
          (row.actualSitesActivatedCum ?? 0) > 0;
        const showSubjects =
          (row.plannedEnrolledCum ?? 0) > 0 || (row.actualEnrolledCum ?? 0) > 0;

        const datePrefix = tooltipViewType === 'monthly' ? '' : 'By ';

        return `
          <div class="activation-tooltip">
            <div class="tooltip-header">
            ${datePrefix}${new DateStringTransform(pointX).formatter(
          SHORT_UI_DISPLAY_DATE,
        )}
            </div>
            ${showSites ? sitesHeader + sitesTable : ''}
            ${showSubjects ? subjectsHeader + subjectsTable : ''}
          </div>
        `;
      },
    };

    const chartData = {
      title: { text: null },
      chart: {
        plotBorderWidth: 1,
        plotBorderColor: '#dcdcdc',
      },
      categories,
      series,
      xAxis,
      tooltip: toolTipData,
      yAxis: enrolmentCurveYAxis,
      exporting: {
        enabled: false,
      },
      custom: {
        combinedData,
        viewType,
      },
      plotOptions,
      legend,
    };
    return chartData;
  }
}

export default EnrolmentCurveChartTransform;
